import axiosInstance from "../../../api/axiosInstance";

export default {
  namespaced: true,
  state: {
    overview: null,
    advisors: [],
    students: [],
    classrooms: [],
    sessionTrends: null,
    loadingOverview: false,
    loadingAdvisors: false,
    loadingStudents: false,
    loadingClassrooms: false,
    noGroup: false,
  },
  getters: {
    overview: (state) => state.overview,
    advisors: (state) => state.advisors,
    students: (state) => state.students,
    classrooms: (state) => state.classrooms,
    sessionTrends: (state) => state.sessionTrends,
    loadingOverview: (state) => state.loadingOverview,
    loadingAdvisors: (state) => state.loadingAdvisors,
    loadingStudents: (state) => state.loadingStudents,
    loadingClassrooms: (state) => state.loadingClassrooms,
    noGroup: (state) => state.noGroup,
  },
  mutations: {
    setNoGroup(state, value) {
      state.noGroup = value;
    },
    setSessionTrends(state, trends) {
      state.sessionTrends = trends;
    },
    setOverview(state, overview) {
      state.overview = overview;
    },
    setAdvisors(state, advisors) {
      state.advisors = advisors;
    },
    setStudents(state, students) {
      state.students = students;
    },
    setLoadingOverview(state, loading) {
      state.loadingOverview = loading;
    },
    setLoadingAdvisors(state, loading) {
      state.loadingAdvisors = loading;
    },
    setLoadingStudents(state, loading) {
      state.loadingStudents = loading;
    },
    setClassrooms(state, classrooms) {
      state.classrooms = classrooms;
    },
    setLoadingClassrooms(state, loading) {
      state.loadingClassrooms = loading;
    },
    upsertAdvisor(state, advisor) {
      const index = state.advisors.findIndex((a) => a.id === advisor.id);
      if (index >= 0) {
        state.advisors.splice(index, 1, advisor);
      } else {
        state.advisors.push(advisor);
      }
    },
    upsertClassroom(state, classroom) {
      const index = state.classrooms.findIndex((c) => c.id === classroom.id);
      if (index >= 0) {
        state.classrooms.splice(index, 1, classroom);
      } else {
        state.classrooms.push(classroom);
      }
    },
    upsertStudent(state, student) {
      const index = state.students.findIndex((s) => s.id === student.id);
      if (index >= 0) {
        state.students.splice(index, 1, student);
      } else {
        state.students.push(student);
      }
    },
    removeAdvisor(state, id) {
      const nid = Number(id);
      state.advisors = state.advisors.filter((advisor) => advisor.id !== nid);
    },
    removeStudent(state, id) {
      const nid = Number(id);
      state.students = state.students.filter((student) => student.id !== nid);
    },
    removeClassroom(state, id) {
      const nid = Number(id);
      state.classrooms = state.classrooms.filter((classroom) => classroom.id !== nid);
      state.students = state.students.filter((student) => student.class_room_id !== nid);
    },
    setStudentsFromMove(state, students) {
      students.forEach((updated) => {
        const index = state.students.findIndex((s) => s.id === updated.id);
        if (index >= 0) {
          state.students.splice(index, 1, updated);
        }
      });
    },
  },
  actions: {
    loadOverview({ commit, dispatch }) {
      commit("setLoadingOverview", true);
      return axiosInstance
        .get("/group/overview")
        .then((response) => {
          commit("setNoGroup", false);
          commit("setOverview", response.data);
          return response;
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            commit("setNoGroup", true);
            return error.response;
          }
          dispatch(
            "showToast",
            { type: "error", message: "Failed to load group overview" },
            { root: true }
          );
          return error.response;
        })
        .finally(() => {
          commit("setLoadingOverview", false);
        });
    },
    loadAdvisors({ commit, dispatch }) {
      commit("setLoadingAdvisors", true);
      return axiosInstance
        .get("/group/advisors")
        .then((response) => {
          commit("setAdvisors", response.data.advisors);
          return response;
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            commit("setNoGroup", true);
            return error.response;
          }
          dispatch(
            "showToast",
            { type: "error", message: "Failed to load advisors" },
            { root: true }
          );
          return error.response;
        })
        .finally(() => {
          commit("setLoadingAdvisors", false);
        });
    },
    inviteAdvisor({ commit, dispatch }, payload) {
      return axiosInstance
        .post("/group/advisors", { advisor: payload })
        .then((response) => {
          commit("upsertAdvisor", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Advisor invited successfully" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.errors?.join(", ") ||
            error.response?.data?.error ||
            "Failed to invite advisor";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    updateAdvisorLicenses({ commit, dispatch }, { id, student_limit_override }) {
      return axiosInstance
        .patch(`/group/advisors/${id}`, {
          advisor: { student_limit_override },
        })
        .then((response) => {
          commit("upsertAdvisor", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Licenses updated" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to update licenses";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    archiveAdvisors({ dispatch }, advisor_ids) {
      return axiosInstance.post("/group/advisors/archive", { advisor_ids })
        .catch((error) => {
          dispatch("showToast", { type: "error", message: error.response?.data?.error || "Failed to archive advisors" }, { root: true });
          return error.response;
        });
    },
    archiveAdvisor({ commit, dispatch }, id) {
      return axiosInstance
        .delete(`/group/advisors/${id}`)
        .then((response) => {
          commit("removeAdvisor", id);
          dispatch(
            "showToast",
            { type: "success", message: "Advisor archived" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to archive advisor";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    updateAdvisorRoles({ commit, dispatch }, { advisor_ids, school_admin }) {
      return axiosInstance
        .patch("/group/advisors/roles", { advisor_ids, school_admin })
        .then((response) => {
          (response.data.advisors || []).forEach((advisor) => {
            commit("upsertAdvisor", advisor);
          });
          dispatch(
            "showToast",
            {
              type: "success",
              message: school_admin
                ? "Admin role assigned"
                : "Admin role removed",
            },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to update admin roles";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    createClassroom({ commit, dispatch }, payload) {
      return axiosInstance
        .post("/group/classrooms", { classroom: payload })
        .then((response) => {
          commit("upsertClassroom", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Classroom created" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to create classroom";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    updateClassroom({ commit, dispatch }, { id, ...payload }) {
      return axiosInstance
        .patch(`/group/classrooms/${id}`, { classroom: payload })
        .then((response) => {
          commit("upsertClassroom", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Classroom updated" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to update classroom";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    fetchAdvisor({ dispatch }, id) {
      return axiosInstance
        .get(`/group/advisors/${id}`)
        .then((response) => response)
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to load advisor";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    sendAdvisorResetPassword({ dispatch }, id) {
      return axiosInstance
        .post(`/group/advisors/${id}/send_reset_password`)
        .then((response) => {
          dispatch(
            "showToast",
            { type: "success", message: "Password reset email sent" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to send password reset email";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    updateAdvisor({ commit, dispatch }, { id, ...payload }) {
      return axiosInstance
        .patch(`/group/advisors/${id}`, { advisor: payload })
        .then((response) => {
          commit("upsertAdvisor", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Advisor updated" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.errors?.join(", ") ||
            error.response?.data?.error ||
            "Failed to update advisor";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    createStudents({ commit, dispatch }, payload) {
      return axiosInstance
        .post("/group/students", { student: payload })
        .then((response) => {
          (response.data.students || []).forEach((student) => {
            commit("upsertStudent", student);
          });
          dispatch(
            "showToast",
            { type: "success", message: "Student created" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to create student";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    fetchStudent({ dispatch }, id) {
      return axiosInstance
        .get(`/group/students/${id}`)
        .then((response) => response)
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to load student";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    archiveStudents({ commit, dispatch }, student_ids) {
      return axiosInstance.post("/group/students/archive", { student_ids })
        .then((response) => {
          response.data.student_ids.forEach((id) => commit("removeStudent", id));
          const count = response.data.student_ids.length;
          dispatch("showToast", {
            type: "success",
            message: `${count} ${count === 1 ? "student" : "students"} archived`,
          }, { root: true });
          return response;
        })
        .catch((error) => {
          dispatch("showToast", {
            type: "error",
            message: error.response?.data?.error || "Failed to archive students",
          }, { root: true });
          return error.response;
        });
    },
    archiveStudent({ commit, dispatch }, id) {
      return axiosInstance
        .post(`/group/students/${id}/archive`)
        .then((response) => {
          commit("removeStudent", id);
          dispatch(
            "showToast",
            { type: "success", message: "Student archived" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to archive student";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    updateStudent({ commit, dispatch }, { id, ...payload }) {
      return axiosInstance
        .patch(`/group/students/${id}`, { student: payload })
        .then((response) => {
          commit("upsertStudent", response.data);
          dispatch(
            "showToast",
            { type: "success", message: "Student updated" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to update student";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    archiveClassroom({ commit, dispatch }, id) {
      return axiosInstance
        .delete(`/group/classrooms/${id}`)
        .then((response) => {
          commit("removeClassroom", id);
          dispatch(
            "showToast",
            { type: "success", message: "Classroom archived" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to archive classroom";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    fetchClassroom({ dispatch }, id) {
      return axiosInstance
        .get(`/group/classrooms/${id}`)
        .then((response) => response)
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to load classroom";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
    loadStudents({ commit, dispatch }) {
      commit("setLoadingStudents", true);
      return axiosInstance
        .get("/group/students")
        .then((response) => {
          commit("setStudents", response.data.students);
          return response;
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            commit("setNoGroup", true);
            return error.response;
          }
          dispatch(
            "showToast",
            { type: "error", message: "Failed to load students" },
            { root: true }
          );
          return error.response;
        })
        .finally(() => {
          commit("setLoadingStudents", false);
        });
    },
    loadClassrooms({ commit, dispatch }) {
      commit("setLoadingClassrooms", true);
      return axiosInstance
        .get("/group/classrooms")
        .then((response) => {
          commit("setClassrooms", response.data.classrooms);
          return response;
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            commit("setNoGroup", true);
            return error.response;
          }
          dispatch(
            "showToast",
            { type: "error", message: "Failed to load classrooms" },
            { root: true }
          );
          return error.response;
        })
        .finally(() => {
          commit("setLoadingClassrooms", false);
        });
    },
    // The panel commits only the latest response so rapid filter changes cannot
    // replace the chart/table with results from an older request.
    loadSessionTrends(_context, params = {}) {
      return axiosInstance
        .get("/group/sessions/trends", { params })
        .catch((error) => error.response);
    },
    moveStudents({ commit, dispatch }, { student_ids, class_room_id, advisor_id }) {
      return axiosInstance
        .post("/group/students/move", { student_ids, class_room_id, advisor_id })
        .then((response) => {
          commit("setStudentsFromMove", response.data.students);
          dispatch(
            "showToast",
            { type: "success", message: "Students moved successfully" },
            { root: true }
          );
          return response;
        })
        .catch((error) => {
          const message =
            error.response?.data?.error || "Failed to move students";
          dispatch(
            "showToast",
            { type: "error", message },
            { root: true }
          );
          return error.response;
        });
    },
  },
};
